import { Component } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { IParamsCollection } from 'src/app/core/jsonapi/interfaces/params-collection';
import { VehiclesService } from '../../vehicles/_services/vehicles.service';
import { WorkerVehiclesService } from '../_services/workers-vehicles.service';
import { WorkerVehicle } from '../_types/worker-vehicle';
import { RxFormBuilder, RxFormGroup } from '@rxweb/reactive-form-validators';
import { FormValidatorService } from 'src/app/core/services/form-validator.service';
import { WorkerBillableItem } from '../_types/worker-billable-item';
import { WorkerBillableItemsService } from '../_services/workers-billable-items.service';
import { BusinessService } from '../../business/_services/business.service';
import { Tax } from '../../taxes/_types/tax';
import { TaxesService } from '../../taxes/_services/taxes.service';
import { LoginService } from '../../login/login.service';
import { WorkerBillableItemType } from '../_types/worker-billable-item-type';
import { WorkerBillableItemTypesService } from '../_services/workers-billable-item-types.service';
import { Zone } from '../../zones/_types/zone';
import { ZonesService } from '../../zones/_services/zones.service';

@Component({
    selector: 'app-workers-billable-items',
    templateUrl: './workers-billable-items.component.html',
    styleUrls: ['./workers-billable-items.component.scss'],
    standalone: false
})
export class WorkersBillableItemsComponent {

  public workerBillableItemFormGroup!: RxFormGroup;

  public workerBillableItem: WorkerBillableItem = new WorkerBillableItem();  
  public selectedType: WorkerBillableItemType | undefined;

  public allowWrite: boolean = false;
  public allowDelete: boolean = false;

  public total: number = 0;
  public subtotal: number = 0;
  public taxAmount: number = 0;
  public taxes: Tax[] = [];
  public loadingTaxes: boolean = false;

  public workerBillableItemTypes: WorkerBillableItemType[] = [];
  public zones: Zone[] = [];

  public IsPayToWorkerOptions: any[] = [
    { name: 'Se paga al operario', value: true },
    { name: 'Se cobra al operario', value: false }
  ];


  constructor(
    private workerBillableItemsService: WorkerBillableItemsService,
    private config: DynamicDialogConfig,
    private messageService: MessageService,
    private dynamicDialogRef: DynamicDialogRef,
    private confirmationService: ConfirmationService,
    private formBuilder: RxFormBuilder,
    private formValidationService: FormValidatorService,
    private workerBillableItemTypesService: WorkerBillableItemTypesService,
    private taxesService: TaxesService,
    private zonesService: ZonesService,
    private loginService: LoginService
  ) {
    this.workerBillableItemFormGroup = this.formBuilder.formGroup<WorkerBillableItem>(this.workerBillableItem) as RxFormGroup;
  }

  async ngOnInit(){
    await this.loadTaxes();
    if (this.config.data.workerBillableItemId) {
      this.loadWorkerBillableItem(this.config.data.workerBillableItemId);
    } else {
      this.workerBillableItem.workerId = this.config.data.workerId;     
    }
    this.loadWorkerBillableItemTypes();

    this.allowWrite = this.loginService.hasPermission("WORKERS.WRITE");
    this.allowDelete = this.loginService.hasPermission("WORKERS.DELETE");
  }

  async loadWorkerBillableItem(id: string) {
    this.workerBillableItem = await firstValueFrom(this.workerBillableItemsService.get(id));
    this.workerBillableItemFormGroup.patchValue(this.workerBillableItem);

    if(this.workerBillableItem.workerVehicleId != null) this.workerBillableItemFormGroup.disable();
    this.calculatePrices();
    //setTimeout(() => this.onWorkerBillableItemTypeChange(), 300 ); Lo comentamos porque si tienen uno mensual con un tipo que NO es mensual al editar lo eliminaba (CASo OTROS, por ejemplo.)
  }


  async loadWorkerBillableItemTypes() {
    let params: IParamsCollection = {
      sort: ["description"]
    }
    let response = await firstValueFrom(this.workerBillableItemTypesService.all(params));    
    this.workerBillableItemTypes = response.data;
  }
  

  async loadTaxes() {
    this.loadingTaxes = true;
    let params: IParamsCollection = {
    }

    let response = await firstValueFrom(this.taxesService.all(params));
    this.taxes = response.data;

    this.loadingTaxes = false;
  }

  async loadZones() {
    let params: IParamsCollection = {
      sort: ["name"],
      page: {
        size: 9999,
        number: 1
      }
    };
    let response = await firstValueFrom(this.zonesService.all(params));
    this.zones = response.data;
  }

  async onWorkerBillableItemTypeChange() {
    let selectedTypeId = this.workerBillableItemFormGroup.controls["workerBillableItemTypeId"].value;
    this.selectedType = this.workerBillableItemTypes.find(type => type.id === selectedTypeId);
    
    if (this.selectedType) {
      this.workerBillableItemFormGroup.get("isPeriodic")?.setValue(this.selectedType.isMonthly);
      if(this.selectedType.code == "ZONE_ATTENDANCE") await this.loadZones();
    }
  }

  async save() {    
    let valid = this.formValidationService.validateForm(this.workerBillableItemFormGroup);    
    if (valid) {
      this.workerBillableItem = Object.assign(this.workerBillableItem, this.workerBillableItemFormGroup.value);

      this.workerBillableItem.nextDate = this.workerBillableItem.startDate;

      await this.workerBillableItemsService.save(this.workerBillableItem).subscribe({
        next: () => {
          this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Concepto facturable asignado correctamente' });
          this.dynamicDialogRef.close();
        },
        error: () => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error al guardar el concepto facturable' });
        }
      });
    } else {
      document.getElementById('form')?.classList.add('formInvalid');
    }
  }
  cancel() {
    this.dynamicDialogRef.close();
  }

  delete() {
    this.confirmationService.confirm({
      message: "¿Está seguro que desea eliminar el concepto facturable?",
      header: "Eliminar concepto",
      acceptLabel: "Aceptar",
      rejectLabel: "Cancelar",
      acceptButtonStyleClass: "p-button-danger",
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.workerBillableItemsService.delete(this.workerBillableItem.id).subscribe({
          next: () => {
            this.cancel();
            this.messageService.add({ closable: true, severity: 'success', detail: "Concepto facturable eliminado correctamente." });
          },
          error: (error: any) => {
            this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
          }
        });
      }
    });
  }

  

  private calObserver?: MutationObserver;

  /**
   * Ancla el panel del calendario a la derecha del input (y a la izquierda si no cabe),
   * centrado verticalmente respecto al campo, en lugar de dejar que PrimeNG lo abra
   * arriba/abajo. Así el alto del mes (p.ej. Febrero, 6 filas) deja de provocar el
   * "salto" de posición del que se queja el cliente.
   * Solo aplica a los dos calendarios con panelStyleClass="cal-lateral".
   */
  posicionarCalendarioLateral(inputId: string) {
    setTimeout(() => {
      const input = document.getElementById(inputId);
      const panel = document.querySelector('.p-datepicker-panel.cal-lateral') as HTMLElement;
      if (!input || !panel) return;

      const r = input.getBoundingClientRect();
      const anchoPanel = panel.offsetWidth;
      const altoPanel = panel.offsetHeight;
      const margen = 8;

      // Por defecto, a la derecha del input; si se saldría del viewport, a la izquierda.
      let left = r.right + margen;
      if (left + anchoPanel > window.innerWidth) {
        left = r.left - anchoPanel - margen;
      }

      // Centrado verticalmente respecto al input, sin salirse del viewport.
      // Se calcula UNA sola vez, al abrir; los meses con más/menos filas crecen hacia
      // abajo, pero la cabecera (y las flechas) se quedan fijas en este top.
      let top = r.top + r.height / 2 - altoPanel / 2;
      top = Math.max(margen, Math.min(top, window.innerHeight - altoPanel - margen));

      const topFijo = `${top + window.scrollY}px`;
      const leftFijo = `${left + window.scrollX}px`;

      const fijar = () => {
        panel.style.top = topFijo;
        panel.style.left = leftFijo;
      };

      fijar();

      // Al navegar de mes, PrimeNG re-renderiza el contenido y recalcula la posición, lo
      // que hacía que el panel saltara. Re-aplicamos SIEMPRE el mismo top/left inicial
      // para que, una vez colocado, se quede ahí.
      this.calObserver?.disconnect();
      this.calObserver = new MutationObserver(() => fijar());
      this.calObserver.observe(panel, { childList: true, subtree: true });
    });
  }

  /** Libera el observer cuando se cierra el panel del calendario. */
  cerrarCalendarioLateral() {
    this.calObserver?.disconnect();
    this.calObserver = undefined;
  }

  calculatePrices() {
    const precioTotalSinImpuestos = this.workerBillableItemFormGroup.controls["quantity"].value * this.workerBillableItemFormGroup.controls["price"].value;    
    this.subtotal = precioTotalSinImpuestos;    

    if(this.workerBillableItemFormGroup.controls["taxId"].value != null){
      let tax:any = this.taxes.find(tax => tax.id == this.workerBillableItemFormGroup.controls["taxId"].value); 
      this.taxAmount = precioTotalSinImpuestos * (tax.percent / 100);          
      this.total = precioTotalSinImpuestos + this.taxAmount;  
    }    
    
  }
}
